import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';
import { useSelector } from 'react-redux';

import { useTrackSidebarInteraction } from '@air/analytics';
import { TreeItemAction } from '@air/component-tree';
import { ChevronDownIcon, EllipsisIcon, GlobeIcon, PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, type DropdownMenuProps, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { Tooltip } from '@air/primitive-tooltip';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { useAirModal } from '@air/provider-modal';

import { useGeneralLibraryMenuOptions } from '~/components/LibraryBeta/GeneralLibraryPage/hooks/useGeneralLibraryMenuOptions';
import { useGeneralLibraryMetadata } from '~/components/LibraryBeta/hooks/useGeneralLibraryMetadata';
import { useGeneralLibraryTitle } from '~/components/LibraryBeta/hooks/useGeneralLibraryTitle';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { boardDragTypes, DragType, type DropData, type GetBoardDragItem } from '~/components/Shared/Drag/dragTypes';
import { WorkspaceSidebarBoardsList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { WorkspaceSidebarLibrariesListItemContextMenu } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesListItemContextMenu';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useWorkspaceBoardRearrange } from '~/hooks/useWorkspaceBoardRearrange';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  centralizedBoardLibrarySelector,
  isCentralizedBoardInGeneralLibrarySelector,
} from '~/store/centralizedBoard/selectors';
import { useWorkspaceBoards } from '~/swr-hooks/boards/useWorkspaceBoards';
import { GeneralLibrary } from '~/utils/librariesUtils';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

const SIDEBAR_GENERAL_LIBRARY_CONTEXT_MENU_LOCK_TOKEN = 'sidebar-general-library-context-menu';

export const WorkspaceSidebarLibrariesListItemGeneral = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const router = useRouter();
  const isCentralizedBoardInGeneralLibrary = useSelector(isCentralizedBoardInGeneralLibrarySelector);
  const centralizedBoardLibraryId = useSelector(centralizedBoardLibrarySelector);
  const { currentWorkspace } = useCurrentWorkspace();
  const { generalLibraryTitle: generalLibraryTitleFromWorkspace } = useGeneralLibraryTitle();
  const generalLibraryTitle = generalLibraryTitleFromWorkspace ?? GeneralLibrary.title;
  const { generalLibraryIcon, generalLibraryColor } = useGeneralLibraryMetadata();
  const open = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: GeneralLibrary.id })?.open);
  const touched = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: GeneralLibrary.id })?.touched);
  const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
  const toggleTreeItemOpen = useWorkspaceSidebarSectionStore((state) => state.toggleTreeItemOpen);
  const {
    data: workspaceBoards,
    hasMore,
    isInitialLoading: isLoadingBoards,
    isLoadingMore,
    loadNextPage,
  } = useWorkspaceBoards({
    workspaceId: currentWorkspace?.id,
    view: 'root',
    includeDirectDescendantBoardCount: true,
    includeDirectDescendantCount: true,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const { menuOptions } = useGeneralLibraryMenuOptions();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const { trackSidebarInteraction } = useTrackSidebarInteraction();
  const { onBoardRearrange, getCanRearrange } = useWorkspaceBoardRearrange({ workspacePermissions: permissions });

  const canCreateGeneralLibraryBoard = canCreateBoard(permissions);

  const isActiveRoute = useMemo(() => router.pathname === Pathname.LibraryGeneral, [router.pathname]);
  const isBoardRoute = useMemo(
    () =>
      router.pathname === Pathname.BoardByBoardId ||
      router.pathname === Pathname.BoardClip ||
      router.pathname === Pathname.BoardCanvas,
    [router.pathname],
  );

  useFetchObjectsPermissions({
    objects: {
      boardIds: workspaceBoards?.map((board) => board.id),
    },
  });

  useEffect(() => {
    /**
     * Early return if `centralizedBoardLibraryId` is defined.
     * This means the user is currently viewing a centralized board library,
     * not the general library. In this case, no sidebar state update is needed.
     * Only proceed if `centralizedBoardLibraryId` is undefined, which indicates
     * the user is in the general library context.
     */
    if (centralizedBoardLibraryId !== undefined) {
      return;
    }

    if (touched) {
      return;
    }

    if (isCentralizedBoardInGeneralLibrary && isBoardRoute) {
      setTreeItem({ id: GeneralLibrary.id, open: true, touched: false });
    }
  }, [centralizedBoardLibraryId, isCentralizedBoardInGeneralLibrary, setTreeItem, touched, isBoardRoute]);

  const onToggleOpen = useCallback(() => {
    toggleTreeItemOpen({ id: GeneralLibrary.id });

    trackSidebarInteraction({
      action: open ? 'collapse' : 'expand',
      type: 'library',
    });
  }, [toggleTreeItemOpen, open, trackSidebarInteraction]);

  const handleLoadNextPage = useCallback(() => {
    loadNextPage();
  }, [loadNextPage]);

  const onCreateBoard = useCallback(() => {
    showCreateBoardModal({
      trackLocation: 'library',
    });

    trackSidebarInteraction({
      action: 'create_board',
      type: 'library',
    });
  }, [showCreateBoardModal, trackSidebarInteraction]);

  const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
    token: 'sidebar-general-library',
  });

  const onDropdownOpenChange = useCallback<Required<DropdownMenuProps>['onOpenChange']>(
    (open) => {
      setIsMenuOpen(open);
      handleLockOpenChange(open);

      if (open) {
        trackSidebarInteraction({
          action: 'open_sub_menu',
          type: 'library',
        });
      }
    },
    [handleLockOpenChange, trackSidebarInteraction],
  );

  const checkIfCanDrop = useCallback(
    (monitor: DropTargetMonitor<GetBoardDragItem>) => {
      const item = monitor.getItem();

      if (!item) {
        return false;
      }

      if (!canCreateBoard(permissions)) {
        return false;
      }

      // Allow dropping boards that aren't already in workspace root (same logic as WorkspaceSidebarWorkspaceBoards)
      if (item?.firstItem?.library?.id || item?.firstItem?.ancestors?.length !== 0) {
        return true;
      }

      return false;
    },
    [permissions],
  );

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: boardDragTypes,
    drop: (): DropData => ({
      type: 'move',
      location: 'sub-nav-rearrange',
    }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: checkIfCanDrop(monitor),
    }),
    canDrop: (_props, monitor) => checkIfCanDrop(monitor),
  });

  const onLibraryClick = useCallback(() => {
    if (touched || open) {
      trackSidebarInteraction({
        action: 'link_click',
        type: 'library',
      });
    } else {
      setTreeItem({ id: GeneralLibrary.id, open: true, touched: false });

      trackSidebarInteraction({
        action: 'link_click_and_expand',
        type: 'library',
      });
    }
  }, [touched, open, setTreeItem, trackSidebarInteraction]);

  const libraryRow = useMemo(
    () => (
      <div
        ref={drop}
        className="group relative flex h-10 ui2026:h-8 shrink-0 cursor-pointer items-center gap-3 ui2026:gap-1.5 ui2026:overflow-hidden rounded-8 p-2 ui2026:py-1 ui2026:pr-1 ui2026:pl-2.5 font-normal ads:text-metadata text-14 text-gray-11 ui2026:text-gray-10 ui2026:leading-[18px] outline-none transition-colors ads:hover:bg-hover-subtle hover:bg-gray-5 ui2026:hover:bg-gray-2 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-gray-5 ads:active:bg-hover-subtle data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:bg-subtle data-[dnd-over=true]:data-[dnd-droppable=true]:bg-gray-3 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:ring-primary data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-inset ads:data-[active=true]:bg-subtle ads:data-[menu-open=true]:bg-subtle data-[menu-open=true]:bg-gray-5 ui2026:data-[active=true]:bg-gray-3 ui2026:data-[menu-open=true]:bg-gray-3 ui2026:data-[active=true]:font-medium ads:data-[active=true]:text-primary ui2026:data-[active=true]:text-gray-12"
        data-active={isActiveRoute}
        data-dnd-over={isOver}
        data-dnd-droppable={canDrop}
        data-menu-open={isMenuOpen || isContextMenuOpen}
        data-open={open}
        data-testid="WORKSPACE_SIDEBAR_LIBRARIES_LIST_ITEM_GENERAL"
        data-title={generalLibraryTitle}
      >
        <div
          className="relative flex size-6 ui2026:size-5 shrink-0 cursor-pointer items-center justify-center ui2026:overflow-hidden rounded-4 ads:bg-ads-gray-4 bg-gray-4 ads:ring-default ring-1 ring-gray-4 ui2026:ring-inset transition-colors ads:group-hover:ring-transparent group-hover:ring-transparent ui2026:group-hover:ring-gray-2 ads:group-data-[active=true]:ring-subtle ads:group-data-[menu-open=true]:ring-subtle ui2026:group-data-[active=true]:ring-gray-3 ui2026:group-data-[menu-open=true]:ring-gray-3"
          style={{ backgroundColor: generalLibraryColor.hex }}
        >
          <span className="text-14 ui2026:text-12">{generalLibraryIcon}</span>

          <button
            aria-expanded={open}
            className="absolute inset-0 z-20 flex size-full items-center justify-center rounded-4 ads:bg-ads-gray-5 bg-gray-5 ads:text-icon-default ui2026:text-gray-11 opacity-0 transition-[background-color,opacity] ads:hover:bg-hover-subtle hover:bg-gray-4 ui2026:hover:bg-gray-3 ads:hover:text-icon-emphasis ui2026:hover:text-gray-12 focus-visible:opacity-100 group-hover:opacity-100"
            data-testid="WORKSPACE_SIDEBAR_GENERAL_LIBRARY_EXPAND_BUTTON"
            onClick={onToggleOpen}
            type="button"
          >
            <VisuallyHidden>{open ? 'Collapse' : 'Expand'} library</VisuallyHidden>
            {ui2026 ? (
              <ChevronDownIcon
                className="text-inherit transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0"
                size="xs"
              />
            ) : (
              <ChevronDownIcon
                className="ads:text-icon-emphasis text-gray-12 transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0"
                size="sm"
              />
            )}
          </button>
        </div>

        <Link
          className="absolute inset-0 z-10 size-full rounded-8 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
          href={{ pathname: Routes.generalLibrary, query: getCanvasQueryParams() }}
          onClick={onLibraryClick}
        >
          <VisuallyHidden>Go to {generalLibraryTitle}</VisuallyHidden>
        </Link>

        <div className="flex grow items-center gap-1">
          <span className="truncate">{generalLibraryTitle}</span>
          <div className="relative z-20 shrink-0">
            <Tooltip
              label={`Users in ${currentWorkspace?.name} are automatically members of this library`}
              side="left"
              align="center"
            >
              <GlobeIcon className="ads:text-icon-subtle text-gray-8" size="sm" />
            </Tooltip>
          </div>
        </div>

        <div className="relative z-20 hidden shrink-0 items-center gap-1 transition-opacity group-focus-within:flex group-hover:flex group-data-[menu-open=true]:flex">
          {!!menuOptions.length && (
            <DropdownMenu
              align="start"
              onOpenChange={onDropdownOpenChange}
              open={isMenuOpen}
              trigger={
                <TreeItemAction aria-label={`${generalLibraryTitle} actions`} data-testid="GENERAL_LIBRARY_MENU_BUTTON">
                  <EllipsisIcon size="sm" />
                </TreeItemAction>
              }
            >
              {renderDropdownItems({ options: menuOptions })}
            </DropdownMenu>
          )}

          {canCreateGeneralLibraryBoard && (
            <TreeItemAction
              aria-label={`Create board in ${generalLibraryTitle}`}
              data-testid="ADD_GENERAL_LIBRARY_BOARD_BUTTON"
              onClick={onCreateBoard}
            >
              <PlusIcon size="sm" />
            </TreeItemAction>
          )}
        </div>
      </div>
    ),
    [
      canCreateGeneralLibraryBoard,
      canDrop,
      currentWorkspace?.name,
      drop,
      generalLibraryColor.hex,
      generalLibraryIcon,
      generalLibraryTitle,
      isActiveRoute,
      isContextMenuOpen,
      isMenuOpen,
      isOver,
      menuOptions,
      onCreateBoard,
      onDropdownOpenChange,
      onLibraryClick,
      onToggleOpen,
      open,
      ui2026,
    ],
  );

  const libraryRowWithContextMenu = useMemo(
    () =>
      ui2026 ? (
        <WorkspaceSidebarLibrariesListItemContextMenu
          lockToken={SIDEBAR_GENERAL_LIBRARY_CONTEXT_MENU_LOCK_TOKEN}
          onOpenChange={setIsContextMenuOpen}
          options={menuOptions}
          title={generalLibraryTitle}
        >
          {libraryRow}
        </WorkspaceSidebarLibrariesListItemContextMenu>
      ) : (
        libraryRow
      ),
    [generalLibraryTitle, libraryRow, menuOptions, ui2026],
  );

  return (
    <div className="flex flex-col gap-1 ui2026:gap-0">
      {libraryRowWithContextMenu}

      {!isLoadingBoards && (workspaceBoards?.length > 0 || hasMore) ? (
        <WorkspaceSidebarCollapsible open={open}>
          <WorkspaceSidebarBoardsList
            boards={workspaceBoards}
            depth={ui2026 ? 1 : 2}
            dragType={DragType.boardNavWorkspace}
            draggable={true}
            droppable={true}
            getCanRearrange={getCanRearrange}
            onBoardRearrange={onBoardRearrange}
            parentBoardId={null}
          />
          {hasMore ? (
            <Button
              appearance="ghost"
              className="ml-9 justify-start"
              color="gray"
              data-testid="WORKSPACE_SIDEBAR_GENERAL_LIBRARY_BOARDS_SHOW_MORE"
              isLoading={isLoadingMore}
              onClick={handleLoadNextPage}
              size="small"
            >
              Show more
            </Button>
          ) : null}
        </WorkspaceSidebarCollapsible>
      ) : null}
    </div>
  );
});

WorkspaceSidebarLibrariesListItemGeneral.displayName = 'WorkspaceSidebarLibrariesListItemGeneral';
